import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_URL } from '../../api';

const FILE_TYPE_COLORS = {
    pdf: 'bg-red-700 text-white',
    html: 'bg-blue-700 text-white',
    epub: 'bg-purple-700 text-white',
    image: 'bg-green-700 text-white',
};
const fileTypeColor = (ft) => FILE_TYPE_COLORS[ft?.toLowerCase()] || 'bg-gray-700 text-white';

const FileContent = ({ work }) => {
    const ft = work.fileType?.toLowerCase();

    if (ft === 'pdf') {
        return (
            <div className="mt-4">
                <iframe
                    src={work.fileUrl}
                    title={work.title}
                    className="w-full h-96 rounded-lg border border-slate-600"
                />
                <a
                    href={work.fileUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-sm bg-amber-600 hover:bg-amber-500 text-white px-4 py-1.5 rounded-full font-medium transition-colors"
                >
                    Download PDF
                </a>
            </div>
        );
    }

    if (ft === 'html') {
        return (
            <div className="mt-4">
                <a
                    href={work.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-sm bg-blue-700 hover:bg-blue-600 text-white px-5 py-2 rounded-full font-medium transition-colors"
                >
                    Open Document
                </a>
                {work.description && (
                    <p className="mt-4 text-gray-300 leading-relaxed">{work.description}</p>
                )}
            </div>
        );
    }

    if (ft === 'epub') {
        return (
            <div className="mt-4">
                <a
                    href={work.fileUrl}
                    download
                    className="inline-block text-sm bg-purple-700 hover:bg-purple-600 text-white px-5 py-2 rounded-full font-medium transition-colors"
                >
                    Download EPUB
                </a>
            </div>
        );
    }

    if (ft === 'image') {
        return (
            <div className="mt-4">
                <img
                    src={work.fileUrl}
                    alt={work.title}
                    className="max-w-full rounded-lg border border-slate-600"
                />
            </div>
        );
    }

    return (
        <div className="mt-4">
            <a
                href={work.fileUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-sm bg-slate-600 hover:bg-slate-500 text-white px-5 py-2 rounded-full font-medium transition-colors"
            >
                Download File
            </a>
        </div>
    );
};

const LibraryWork = () => {
    const { id } = useParams();
    const [work, setWork] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const fetchWork = async () => {
            setIsLoading(true);
            setError(null);
            setNotFound(false);
            try {
                const res = await fetch(`${API_URL}/library/works/${id}`);
                if (res.status === 404) {
                    setNotFound(true);
                    return;
                }
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                const data = await res.json();
                setWork(data.work || data);
            } catch (err) {
                setError('Unable to load this work at this time.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchWork();
    }, [id]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" aria-label="Loading" />
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="min-h-screen py-12 px-6">
                <div className="max-w-3xl mx-auto">
                    <nav className="mb-6 text-sm text-gray-400" aria-label="Breadcrumb">
                        <Link to="/library" className="hover:text-amber-400 transition-colors">Library</Link>
                        <span className="mx-2 text-gray-600">&rsaquo;</span>
                        <Link to="/library/works" className="hover:text-amber-400 transition-colors">Dharma Works</Link>
                        <span className="mx-2 text-gray-600">&rsaquo;</span>
                        <span className="text-gray-200">Not Found</span>
                    </nav>
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-10 text-center">
                        <p className="text-white text-lg mb-4">This work could not be found.</p>
                        <Link to="/library/works" className="text-amber-400 hover:underline text-sm">
                            Back to Dharma Works
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen py-12 px-6">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-10 text-center">
                        <p className="text-gray-400">{error}</p>
                        <Link to="/library/works" className="mt-4 inline-block text-amber-400 hover:underline text-sm">
                            Back to Dharma Works
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-3xl mx-auto">
                {/* Breadcrumb */}
                <nav className="mb-6 text-sm text-gray-400" aria-label="Breadcrumb">
                    <Link to="/library" className="hover:text-amber-400 transition-colors">Library</Link>
                    <span className="mx-2 text-gray-600">&rsaquo;</span>
                    <Link to="/library/works" className="hover:text-amber-400 transition-colors">Dharma Works</Link>
                    <span className="mx-2 text-gray-600">&rsaquo;</span>
                    <span className="text-gray-200">{work?.title}</span>
                </nav>

                {/* Back button */}
                <Link
                    to="/library/works"
                    className="inline-block mb-6 text-sm bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-600/30 px-4 py-1.5 rounded-full transition-colors"
                >
                    &larr; Back to Dharma Works
                </Link>

                <div className="bg-slate-900/70 backdrop-blur-sm border border-slate-700 rounded-xl p-7">
                    {/* Title */}
                    <h1 className="text-2xl font-bold text-white mb-2">{work?.title}</h1>

                    {work?.author && (
                        <p className="text-gray-400 text-sm mb-4">by {work.author}</p>
                    )}

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {work?.tradition && (
                            <span className="text-xs bg-slate-700 text-gray-300 px-2 py-0.5 rounded-full">
                                {work.tradition}
                            </span>
                        )}
                        {work?.language && (
                            <span className="text-xs bg-slate-700 text-gray-300 px-2 py-0.5 rounded-full">
                                {work.language}
                            </span>
                        )}
                        {work?.fileType && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${fileTypeColor(work.fileType)}`}>
                                {work.fileType.toUpperCase()}
                            </span>
                        )}
                    </div>

                    {/* Description */}
                    {work?.description && (
                        <p className="text-gray-300 leading-relaxed mb-2">{work.description}</p>
                    )}

                    {/* Tags */}
                    {work?.tags && work.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {work.tags.map((tag) => (
                                <span key={tag} className="text-xs bg-slate-800 text-gray-400 px-2 py-0.5 rounded-full border border-slate-600">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* File content */}
                    {work?.fileUrl && <FileContent work={work} />}
                </div>
            </div>
        </div>
    );
};

export default LibraryWork;
